import React, { useState, useEffect } from "react";
import { Formik, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputLabel,
  FormControl,
  Tooltip,
} from "@mui/material";
import Chip from "@mui/material/Chip";
import OutlinedInput from "@mui/material/OutlinedInput";
import MenuItem from "@mui/material/MenuItem";
import Box from "@mui/material/Box";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import {
  TextInputComponent,
  SearchInputComponent,
  SelectInputDropDownComponent,
} from "../../../../../common/components";
import DatePickerComponent from "../../../../../common/components/date-picker/date-picker-compoenent";
import CloseIcon from "../../../../../assets/icons/closeIcon.svg";
import infoIcon from "../../../../../assets/icons/info-icon.svg";
import "./company-dashboard-add-initiative-popup.scss";
import { StatusOption } from "../company-dashboard-add-object-popup/company-dashboard-add-object-popup";
import dayjs from "dayjs";
import { Add, Close } from "@mui/icons-material";
import { OverlayScrollbarsComponent } from "overlayscrollbars-react";
import { debounce } from "lodash";
import { getInitiativeTitle } from "../../../../../api/title-search";

const CompanyDashboardAddInitiativePopup: any = (props: any) => {
  const {
    isOpenAddPopup,
    onCancel,
    employeesList,
    statusListInitiatives,
    onSave,
    departmentsList,
  } = props;
  const subDepartmentList = [
    { color: "#FCE8D2", text: "Sales", id: [1, 11, 21, 31, 41, 51] },
    { color: "#D6ECD2", text: "Marketing", id: [2, 12, 22, 32, 42, 52] },
    { color: "#FFD9E7", text: "AI Team", id: [3, 13, 23, 33, 43, 53] },
    { color: "#DEC3A6", text: "Finance", id: [4, 14, 24, 34, 44, 54] },
    { color: "#DDEAFF", text: "Accounting", id: [5, 15, 25, 35, 45, 55] },
    { color: "#CAC3D6", text: "IT", id: [6, 16, 26, 36, 46, 56] },
    {
      color: "#C3E0DF",
      text: "Engineering & Transformation",
      id: [7, 17, 27, 37, 47, 57],
    },
    { color: "#BAD1B6", text: "PMO", id: [8, 18, 28, 38, 48, 58] },
    { color: "#FFFDD9", text: "Data", id: [9, 19, 29, 39, 49, 59] },
    {
      color: "#E0C3CE",
      text: "Technology and Data",
      id: [10, 20, 30, 40, 50, 60],
    },
  ];
  const userID = JSON.parse(
    localStorage.getItem("userDetails") || "{}",
  )?.employeeId;
  const [mainDepartments, setMainDepartments] = useState<any>([]);
  const [open, setOpen] = useState(false);
  const [titleSuggestions, setTitleSuggestions] = useState<any[]>([]);
  const [loadingTitles, setLoadingTitles] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [autocompleteOpen, setAutocompleteOpen] = useState(false);
  const [employeesListInfo, setEmployeesListInfo] = useState<any[]>([]);
  const [loginInfo, setLoginInfo] = useState<any>({});

  useEffect(() => {
    setEmployeesListInfo(
      employeesList?.map((emp: any) => {
        if (emp?.employeeId === userID) {
          setLoginInfo({
            value: emp.employeeFullName,
            label: emp.employeeFullName,
            image: emp.employeeImage || null,
            avatar: emp.employeeImage ? null : emp.employeeFullName,
            role: "Owner",
            employeeId: emp.employeeId,
            disabled: emp.employeeId === userID,
          });
        }
        return {
          value: emp.employeeFullName,
          label: emp.employeeFullName,
          image: emp.employeeImage || null,
          avatar: emp.employeeImage ? null : emp.employeeFullName,
          role: emp.employeeId === userID ? "Owner" : "PM",
          employeeId: emp.employeeId,
          disabled: emp.employeeId === userID,
        };
      }) || [],
    );
  }, [employeesList]);

  useEffect(() => {
    setMainDepartments(
      departmentsList?.map((dept: any) => {
        return {
          color:
            subDepartmentList.find((sub: any) =>
              sub.id.includes(dept.departmentId),
            )?.color || "#E8F0FE",
          text: dept.departmentName,
          id: dept.departmentId,
          employeeId: dept.employeeId,
        };
      }) || [],
    );
  }, [departmentsList]);

  // Clear search state when popup closes
  React.useEffect(() => {
    if (!isOpenAddPopup) {
      setTitleSuggestions([]);
      setHasSearched(false);
      setAutocompleteOpen(false);
      setLoadingTitles(false);
    }
  }, [isOpenAddPopup]);

  // Debounced function to search for initiative titles
  const searchInitiativeTitles = React.useMemo(
    () =>
      debounce(async (searchValue: string) => {
        if (!searchValue || searchValue.trim().length < 2) {
          setTitleSuggestions([]);
          setHasSearched(false);
          return;
        }

        setLoadingTitles(true);
        setHasSearched(true);
        try {
          const response = await getInitiativeTitle({ title: searchValue });
          const initiatives = response?.data?.data || [];
          setTitleSuggestions(initiatives);
        } catch (error: any) {
          setTitleSuggestions([]);
          console.error("Error fetching initiative titles:", error);
        } finally {
          setLoadingTitles(false);
        }
      }, 500),
    [],
  );

  const validationSchema = Yup.object().shape({
    title: Yup.string().trim().required("Title is required"),

    owner: Yup.array().min(1, "Owner is required"),

    startDate: Yup.date()
      .nullable()
      .transform((value, originalValue) => {
        // Ensure empty string => null
        return originalValue === "" ? null : value;
      })
      .typeError("Please select a valid Start Date")
      .min(new Date(1900, 0, 1), "Start Date must be after 1900")
      .required("Start Date is required")
      .test(
        "start-before-end",
        "Start Date cannot be after End Date",
        function (value, context) {
          const { endDate } = context.parent;
          if (value && endDate) {
            return value <= endDate;
          }
          return true;
        },
      ),
    endDate: Yup.date()
      .nullable()
      .transform((value, originalValue) => {
        return originalValue === "" ? null : value;
      })
      .typeError("Please select a valid End Date")
      .min(new Date(1900, 0, 1), "End Date must be after 1900")
      .required("End Date is required")
      .test(
        "end-after-start",
        "End Date cannot be earlier than Start Date",
        function (value, context) {
          const { startDate } = context.parent;
          if (value && startDate) {
            return value >= startDate;
          }
          return true;
        },
      ),

    weightage: Yup.number()
      .typeError("Weightage must be a number")
      .optional()
      .min(0, "Weightage must be at least 0")
      .max(100, "Weightage cannot exceed 100"),
    threshold: Yup.number()
      .typeError("Threshold must be a number") // catches string input
      .min(0, "Threshold cannot be negative")
      .max(100, "Threshold cannot exceed 100")
      .required("Threshold is required"),
    participatingDepartments: Yup.array()
      .of(
        Yup.object({
          id: Yup.number().required("Department id is required"),
          text: Yup.string().required("Department name is required"),
        }),
      )
      .min(1, "At least one department is required")
      .required("Participating Department is required"),

    status: Yup.mixed().nullable().required("Status is required"),
  });
  const [searchText, setSearchText] = useState("");

  const filteredDepartments = mainDepartments.filter((dept: any) =>
    dept.text.toLowerCase().includes(searchText.toLowerCase()),
  );

  const getFilteredValues = (filteredValues: any) => {
    return filteredValues.map((name: any) => {
      const emp = employeesListInfo?.find((emp: any) => emp.value === name);
      return {
        ownerId: emp?.employeeId,
        role: emp?.role || null,
      };
    });
  };

  const handleOwnerChange = (
    setFieldValue: any,
    values: any,
    department: any,
  ) => {
    if (
      !values.owners?.find((id: any) => id?.ownerId === department.employeeId)
    ) {
      setFieldValue("owner", [
        ...(values?.owner || []),
        employeesList?.find(
          (emp: any) => emp.employeeId === department?.employeeId,
        )?.employeeFullName,
      ]);
      setFieldValue("owners", [
        ...(values?.owners ?? []),
        { ownerId: department.employeeId, role: "Owner" },
      ]);
      setEmployeesListInfo(
        employeesListInfo?.map((dept: any) => {
          if (dept?.employeeId === department.employeeId) {
            return {
              ...dept,
              role: "Owner",
            };
          }
          return dept;
        }),
      );
    }
  };

  const slotsPaperTitle = (props: any, values: any) => {
    const showNoResults =
      !loadingTitles &&
      hasSearched &&
      titleSuggestions.length === 0 &&
      values.title.trim().length >= 2;

    const showLoading = loadingTitles;

    const showSearchPrompt = titleSuggestions.length === 0;

    const renderContent = () => {
      if (showLoading) {
        return (
          <div
            style={{
              padding: "8px 16px",
              color: "#666",
              fontSize: "14px",
              textAlign: "center",
            }}
          >
            Searching...
          </div>
        );
      }
      if (showNoResults) {
        return (
          <div
            style={{
              padding: "8px 16px",
              color: "#666",
              fontSize: "14px",
              textAlign: "center",
            }}
          >
            No results found.
          </div>
        );
      }
      if (showSearchPrompt) {
        return (
          <div
            style={{
              padding: "8px 16px",
              color: "#666",
              fontSize: "14px",
              textAlign: "center",
            }}
          >
            Search
          </div>
        );
      }
      return props.children;
    };

    return (
      <div
        {...props}
        style={{
          backgroundColor: "#fff",
          boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1)",
          borderRadius: "8px",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "4px 8px",
            background: "#fff",
            borderBottom: "0.5px solid #eee",
          }}
        >
          <div className="" style={{ fontSize: "14px" }}>
            <InputLabel style={{ fontSize: "14px" }}>
              Existing Initiatives
            </InputLabel>
          </div>
          <button
            style={{
              background: "transparent",
              border: "none",
              padding: 0,
              cursor: "pointer",
            }}
            onClick={(e) => {
              e.stopPropagation();
              setTitleSuggestions([]);
              setHasSearched(false);
              setAutocompleteOpen(false);
            }}
          >
            <img
              alt="Close"
              src={CloseIcon}
              style={{
                cursor: "pointer",
                color: "#555",
                margin: "4px 0",
              }}
            />
          </button>
        </div>
        {renderContent()}
      </div>
    );
  };

  const slotsPaperStatus = (props: any, values: any) => {
    return (
      <div
        {...props}
        style={{
          backgroundColor: "#fff",
          boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1)",
          borderRadius: "8px",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            padding: "4px 8px",
            background: "#fff",
          }}
        >
          <Close
            sx={{
              cursor: "pointer",
              color: "#555",
              margin: "4px 0",
              "&:hover": { color: "#000" },
            }}
            onClick={(e) => {
              e.stopPropagation();
              const dropdown = document.querySelector(
                ".MuiAutocomplete-popper",
              ) as HTMLElement;
              if (dropdown) dropdown.style.display = "none";
            }}
          />
        </div>

        {props.children}
      </div>
    );
  };

  const getOwnerFormattedValues = (ownerFilteredValues: any, deptFilteredValues: any) => {
    return ownerFilteredValues?.filter((name: any) => !!name)?.map((name: any) => {
      const empInfo = employeesList?.find((emp: any) => emp.employeeId === name?.ownerId);
      const isDepartmentOwner = deptFilteredValues.some((dept: any) => dept?.id === empInfo?.departmentId);
      return {
        ...name,
        isDepartmentOwner: isDepartmentOwner,
      };
    });
  };
  return (
    <Dialog
      open={isOpenAddPopup}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      className={`company-dashboard-add-popup-container `}
      onClose={(event, reason) => {
        if (reason === "backdropClick") {
          return;
        }
        onCancel();
      }}
      fullWidth
      maxWidth="md"
      slotProps={{
        paper: {
          sx: {
            width: "90vw",
            maxWidth: "900px",
            height: "80vh",
          },
        },
      }}
    >
      <DialogTitle
        id="alert-dialog-title"
        sx={{
          position: "sticky",
          top: -10,
          zIndex: 10,
          background: "#f7f8fb",
        }}
      >
        <div className="header-wrapper">
          <div className="header-wrapper-text">Add Initiative</div>
          <div className="image-wrapper">
            <button
              style={{
                background: "transparent",
                border: "none",
                padding: 0,
                cursor: "pointer",
              }}
              onClick={onCancel}
            >
              <img
                src={CloseIcon}
                className="close-icon"
                style={{ cursor: "pointer" }}
                alt="close"
              />
            </button>
          </div>
        </div>
      </DialogTitle>
      <DialogContent>
        <Formik
          initialValues={{
            selectedOption: "Objective",
            owner: [loginInfo?.value],
            owners: [
              {
                ownerId: loginInfo?.employeeId,
                role: "Owner",
              },
            ],
            title: "",
            comments: "",
            status:
              statusListInitiatives.find(
                (s: StatusOption) => s.statusName === "Not Started",
              ) || null,
            startDate: null,
            endDate: null,
            weightage: "",
            threshold: 15,
            participatingDepartments: [] as { id: number; text: string }[],
          }}
          validationSchema={validationSchema}
          validateOnChange={true}
          enableReinitialize={true}
          onSubmit={async (values, { resetForm }) => {
            const newInitiative = {
              ...values,
              participatingDepartments: values.participatingDepartments.map(
                (dept) => dept.id,
              ), // ✅ only ids sent
              owners: getOwnerFormattedValues([
                ...values.owners.filter(
                  (v: any) => v?.ownerId !== userID && v?.ownerId,
                ),
                { ownerId: userID, role: "Owner" },
              ], values.participatingDepartments),
            };
            try {
              await onSave(newInitiative);
              onCancel();
              resetForm();
            } catch (error) {
              console.error(error);
            }
          }}
        >
          {({
            values,
            setFieldValue,
            touched,
            errors,
            validateField,
            isValid,
            submitCount,
          }) => (
            <Form>
              <OverlayScrollbarsComponent
                className="create-lead-capture-popup-confirmation-description-checkbox-input"
                options={{
                  scrollbars: {
                    theme: "os-theme-dark",
                    autoHide: "scroll",
                    autoHideDelay: 400,
                  },
                }}
              >
                <div className="form-group form-text-input-component">
                  <InputLabel>Title</InputLabel>
                  <Autocomplete
                    freeSolo
                    options={titleSuggestions}
                    getOptionLabel={(option: any) =>
                      typeof option === "string" ? option : option.title || ""
                    }
                    loading={loadingTitles}
                    filterOptions={(x) => x}
                    open={autocompleteOpen}
                    onOpen={() => {
                      // Only open if there's meaningful content to show
                      if (
                        values.title.trim().length >= 2 ||
                        titleSuggestions.length > 0 ||
                        loadingTitles
                      ) {
                        setAutocompleteOpen(true);
                      }
                    }}
                    onClose={(event, reason) => {
                      // Always close on blur (clicking outside) or escape key
                      if (reason === "blur" || reason === "escape") {
                        setAutocompleteOpen(false);
                        return;
                      }

                      // For other reasons, keep open if there are results, loading, or no results after search
                      const shouldStayOpen =
                        titleSuggestions.length > 0 ||
                        loadingTitles ||
                        (hasSearched &&
                          !loadingTitles &&
                          values.title.trim().length >= 2);

                      // Only close if shouldn't stay open (not when showing no results)
                      if (!shouldStayOpen) {
                        setAutocompleteOpen(false);
                      }
                    }}
                    disableClearable
                    value={values.title}
                    inputValue={values.title}
                    onInputChange={(event, newInputValue, reason) => {
                      if (reason === "input") {
                        setFieldValue("title", newInputValue);
                        searchInitiativeTitles(newInputValue);
                        // Open dropdown when typing with enough characters
                        if (newInputValue.trim().length >= 2) {
                          setAutocompleteOpen(true);
                        } else {
                          // Close dropdown when text is cleared or too short
                          setAutocompleteOpen(false);
                          setTitleSuggestions([]);
                          setHasSearched(false);
                        }
                      }
                    }}
                    onChange={(event) => {
                      // Prevent selection - only allow typing
                      event.preventDefault();
                    }}
                    onBlur={() => {
                      // Only clear if we're not showing no results message
                      setTimeout(() => {
                        const shouldKeepOpen =
                          titleSuggestions.length > 0 ||
                          loadingTitles ||
                          (hasSearched &&
                            !loadingTitles &&
                            values.title.trim().length >= 2);

                        if (!shouldKeepOpen) {
                          setTitleSuggestions([]);
                          setHasSearched(false);
                          setAutocompleteOpen(false);
                        }
                      }, 200);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Enter Title"
                        slotProps={{
                          htmlInput: {
                            ...params.inputProps,
                            maxLength: 150,
                          },
                        }}
                      />
                    )}
                    renderOption={(props, option: any) => (
                      <Tooltip title="Selection is turned off for this field.">
                        <li
                          {...props}
                          style={{
                            cursor: "not-allowed",
                            padding: "8px 16px",
                            opacity: 0.7,
                            backgroundColor:
                              values?.title?.trim()?.toLowerCase() ===
                              option.title?.trim()?.toLowerCase()
                                ? "#ddddfa"
                                : "transparent !important",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor =
                              values?.title?.trim()?.toLowerCase() ===
                              option.title?.trim()?.toLowerCase()
                                ? "#ddddfa"
                                : "transparent !important";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor =
                              values?.title?.trim()?.toLowerCase() ===
                              option.title?.trim()?.toLowerCase()
                                ? "#ddddfa"
                                : "transparent !important";
                          }}
                        >
                          <div
                            style={{ fontSize: "14px", pointerEvents: "none" }}
                          >
                            {option.title}
                          </div>
                        </li>
                      </Tooltip>
                    )}
                    noOptionsText={(() => {
                      if (loadingTitles) return "Searching...";
                      if (values.title.length < 2)
                        return "Type at least 2 characters";
                      return "No results found.";
                    })()}
                    slotProps={{
                      listbox: {
                        style: {
                          maxHeight: "200px",
                        },
                      },
                    }}
                    slots={{ paper: (props) => slotsPaperTitle(props, values) }}
                  />
                  <ErrorMessage
                    name="title"
                    component="div"
                    className="error-message"
                  />
                </div>
                <div className="form-group">
                  <div
                    className={`form-text-input-component content-milestones-participating-departments-dropdown`}
                  >
                    <InputLabel id="participating-department(s)">
                      Participating Department(s)
                    </InputLabel>

                    <FormControl
                      variant="standard"
                      sx={{ minWidth: 120, width: "100%" }}
                    >
                      <Select
                        open={open}
                        onOpen={() => setOpen(true)}
                        onClose={() => setOpen(false)}
                        labelId="participating-department(s)"
                        input={<OutlinedInput label="Tag" />}
                        id="participating-department(s)"
                        value={values.participatingDepartments}
                        multiple
                        renderValue={(
                          selected: { id: number; text: string }[],
                        ) => {
                          return (
                            <Box
                              sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 0.5,
                              }}
                            >
                              {selected?.map((dept) => (
                                <Chip
                                  key={dept.id}
                                  sx={{
                                    backgroundColor: mainDepartments?.find(
                                      (d: any) =>// NOSONAR 
                                      d.id === dept.id,
                                    )?.color,
                                    paddingLeft: "2px",
                                    paddingRight: "2px",
                                  }}
                                  onDelete={(e) => {// NOSONAR
                                    e.stopPropagation();
                                    setFieldValue(
                                      "participatingDepartments",
                                      values.participatingDepartments.filter(
                                        (d) => d.id !== dept.id,
                                      ),
                                    );
                                  }}
                                  deleteIcon={
                                    <img
                                      src={CloseIcon}
                                      style={{ width: "16px", height: "16px" }}
                                      alt="close"
                                    />
                                  }
                                  label={dept.text}
                                  onMouseDown={(e) =>// NOSONAR
                                   e.stopPropagation()}
                                />
                              ))}
                            </Box>
                          );
                        }}
                        MenuProps={{
                          slotProps: {
                            paper: {
                              style: {
                                maxHeight: 48 * 4.5,
                                width: 250,
                                padding: 0,
                              },
                            },
                          },
                        }}
                        label=""
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "flex-end",
                            padding: "4px 8px",
                            backgroundColor: "#fff",
                          }}
                        >
                          <button
                            style={{
                              background: "transparent",
                              border: "none",
                              padding: 0,
                              cursor: "pointer",
                            }}
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpen(false);
                            }}
                          >
                            <img
                              alt="Close"
                              src={CloseIcon}
                              style={{
                                cursor: "pointer",
                              }}
                            />
                          </button>
                        </div>
                        <MenuItem
                          value=""
                          className="company-dashboard-initiative-drawer-side-bar-content-milestones-edit-section "
                          disableRipple
                          sx={{
                            "&:hover": {
                              backgroundColor: "transparent !important",
                            },
                            "&.Mui-focusVisible": {
                              backgroundColor: "transparent !important",
                            },
                          }}
                        >
                          <OverlayScrollbarsComponent
                            style={{ height: "300px", width: "100%" }}
                            className="company-dashboard-initiative-drawer-side-bar-content-milestones-edit"
                            options={{
                              scrollbars: {
                                theme: "os-theme-dark",
                                autoHide: "scroll",
                                autoHideDelay: 400,
                              },
                            }}
                          >
                            <div className="company-dashboard-initiative-drawer-side-bar-content-milestones-edit-search">
                              <SearchInputComponent
                                placeholder="Search Departments"
                                onSearch={(value: string) =>
                                  setSearchText(value)
                                }
                                isReset={false}
                                ref={null}
                              />
                            </div>
                            <div className="company-dashboard-initiative-drawer-side-bar-content-milestones-content">
                              {filteredDepartments.length > 0 ? (
                                filteredDepartments.map(
                                  (department: any, index: number) => {
                                    const isSelected =
                                      values.participatingDepartments.some(
                                        (d: { id: number; text: string }) =>
                                          d.id === department.id,
                                      );
                                    return (
                                      <button
                                        className="company-dashboard-initiative-drawer-side-bar-content-milestones-content-item"
                                        key={index + 1}
                                        style={{
                                          backgroundColor: department?.color,
                                        }}
                                        type="button"
                                        onClick={() => {
                                          if (
                                            !values.participatingDepartments.some(
                                              (d) =>// NOSONAR
                                               d.id === department.id,
                                            )
                                          ) {
                                            setFieldValue(
                                              "participatingDepartments",
                                              [
                                                ...values.participatingDepartments,
                                                {
                                                  id: department.id,
                                                  text: department.text,
                                                }, // ✅ push object
                                              ],
                                            );
                                            handleOwnerChange(
                                              setFieldValue,
                                              values,
                                              department,
                                            );
                                          }
                                        }}
                                      >
                                        <div className="company-dashboard-initiative-drawer-side-bar-content-milestones-content-item-label">
                                          {department?.text}
                                        </div>
                                        <button
                                          type="button"
                                          style={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "right",
                                            border: "none",
                                            background: "transparent",
                                            cursor: "pointer",
                                            padding: "0px",
                                            marginLeft: "8px",
                                          }}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            if (isSelected) {
                                              setFieldValue(
                                                "participatingDepartments",
                                                values.participatingDepartments.filter(
                                                  (d: { id: number }) =>// NOSONAR
                                                  d.id !== department.id,
                                                ),
                                              );
                                            } else {
                                              setFieldValue(
                                                "participatingDepartments",
                                                [
                                                  ...values.participatingDepartments,
                                                  {
                                                    id: department.id,
                                                    text: department.text,
                                                  },
                                                ],
                                              );
                                              if (department.employeeId) {
                                                handleOwnerChange(
                                                  setFieldValue,
                                                  values,
                                                  department,
                                                );
                                              }
                                            }
                                          }}
                                        >
                                          {isSelected ? (
                                            <Close
                                              style={{
                                                width: 16,
                                                height: 16,
                                                cursor: "pointer",
                                              }}
                                            />
                                          ) : (
                                            <Add
                                              style={{
                                                width: 16,
                                                height: 16,
                                                cursor: "pointer",
                                              }}
                                            />
                                          )}
                                        </button>{" "}
                                      </button>
                                    );
                                  },
                                )
                              ) : (
                                <div
                                  style={{
                                    padding: "8px",
                                    color: "#888",
                                    textAlign: "center",
                                    width: "100%",
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                  }}
                                >
                                  Participating Department(s) Not Found
                                </div>
                              )}
                            </div>
                          </OverlayScrollbarsComponent>{" "}
                        </MenuItem>
                      </Select>
                    </FormControl>
                    <ErrorMessage
                      name="participatingDepartments"
                      component="div"
                      className="error-message"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <SelectInputDropDownComponent
                    fieldName="owner"
                    placeholder="Select Owner"
                    label="Owner"
                    isOwnerDropdown={true}
                    optional={false}
                    disabled={false}
                    multiple={true}
                    options={employeesListInfo}
                    onRoleChange={(event: any, newValue: any, role: any) => {
                      setEmployeesListInfo(
                        employeesListInfo?.map((dept: any) => {
                          if (dept?.employeeId === role?.employeeId) {
                            return {
                              ...dept,
                              role: newValue?.role,
                            };
                          }
                          return dept;
                        }),
                      );
                      const updatedOwners = values.owners.map((owner: any) => {
                        if (owner?.ownerId === role?.employeeId) {
                          return {
                            ...owner,
                            role: newValue?.role,
                          };
                        }
                        return owner;
                      });
                      setFieldValue("owners", updatedOwners);
                    }}
                    value={values.owner}
                    onChange={(selectedValues: any[]) => {
                      const filteredValues = selectedValues.filter(
                        (v) => v != null,
                      );
                      setFieldValue("owner", filteredValues);
                      setFieldValue(
                        "owners",
                        getFilteredValues(filteredValues),
                      );
                      // Trigger validation if form has been submitted before
                      if (touched.owner) {
                        setTimeout(() => {
                          validateField("owner");
                        }, 0);
                      }
                    }}
                  />
                  <ErrorMessage
                    name={"owner"}
                    component="div"
                    className="error-message"
                  />
                </div>
                <div className="form-group">
                  <DatePickerComponent
                    fieldName={"startDate"}
                    label="Start Date"
                    value={values?.startDate}
                    onChange={(e: any) => {
                      setFieldValue("startDate", e);
                    }}
                    minDate={dayjs("1900-01-01")} // 👈 disables before 1900
                    maxDate={values.endDate ? dayjs(values.endDate) : undefined} // 👈 disables after endDate
                  />
                </div>
                <div className="form-group">
                  <DatePickerComponent
                    fieldName={"endDate"}
                    label="End Date"
                    value={values?.endDate}
                    onChange={(e: any) => {
                      setFieldValue("endDate", e);
                    }}
                    minDate={
                      values.startDate
                        ? dayjs(values.startDate)
                        : dayjs("1900-01-01")
                    }
                  />
                </div>
                <div className="form-group">
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    <InputLabel>Threshold</InputLabel>
                    <Tooltip
                      title="The maximum lead time allowed (in %age) before an Initiative is marked delayed."
                      arrow
                      placement="right-start"
                    >
                      <img
                        src={infoIcon}
                        alt="info"
                        style={{
                          width: "16px",
                          height: "16px",
                          cursor: "pointer",
                        }}
                      />
                    </Tooltip>
                  </div>
                  <TextInputComponent
                    type="number"
                    fieldName="threshold"
                    label=""
                    placeholder="0.00"
                    optional={false}
                    disabled={false}
                    value={values.threshold}
                    onChange={(e: any) => {
                      if (
                        e?.target?.value === "" ||
                        /^\d+$/.test(e?.target?.value)
                      ) {
                        const value = e.target.value;
                        setFieldValue(
                          "threshold",
                          value === "" ? "" : Number(value),
                        );
                      }
                    }}
                    blockInvalidChars={true}
                    isLocked={true}
                    lockedIcons={
                      <i
                        className="fa fa-percent"
                        style={{ color: "#0000008a", fontWeight: "normal" }}
                      />
                    }
                  />
                </div>

                <div className="form-group form-group-status form-text-input-component">
                  <InputLabel>Status</InputLabel>

                  <Autocomplete<StatusOption>
                    value={values.status}
                    onChange={(event, newValue) => {
                      setFieldValue("status", newValue);
                    }}
                    options={statusListInitiatives || []}
                    getOptionLabel={(option) => option.statusName}
                    isOptionEqualToValue={(option, value) =>
                      option.statusId === value.statusId
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        error={touched.status && Boolean(errors.status)}
                      />
                    )}
                    sx={{
                      "& .MuiAutocomplete-option": {
                        fontSize: "14px",
                        fontFamily: "Manrope, sans-serif",
                      },
                    }}
                    slots={{
                      paper: (props) => slotsPaperStatus(props, values),
                    }}
                    slotProps={{
                      popper: {
                        sx: {
                          "& .MuiAutocomplete-option": {
                            fontSize: "14px",
                            fontFamily: "Manrope, sans-serif",
                          },
                        },
                      },
                    }}
                  />
                  <ErrorMessage
                    name="status"
                    component="div"
                    className="error-message"
                  />
                </div>
              </OverlayScrollbarsComponent>
              <DialogActions
                sx={{
                  position: "sticky",
                  bottom: "-10px",
                  zIndex: 10,
                  background: "#f7f8fb",
                }}
              >
                <div className="action-btn-wrapper">
                  <button
                    onClick={onCancel}
                    type="button"
                    className="cancel-btn"
                  >
                    Cancel
                  </button>
                  {submitCount > 0 && !isValid ? (
                    <Tooltip title="Please resolve all errors before submitting.">
                      <span>
                        <button
                          type="submit"
                          className="action-btn"
                          disabled
                          style={{
                            opacity: 0.5,
                            cursor: "not-allowed",
                          }}
                        >
                          Add
                        </button>
                      </span>
                    </Tooltip>
                  ) : (
                    <button
                      type="submit"
                      className="action-btn"
                      style={{
                        opacity: 1,
                        cursor: "pointer",
                      }}
                    >
                      Add
                    </button>
                  )}
                </div>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

export default CompanyDashboardAddInitiativePopup;
